import { createBrowserClient } from '@supabase/ssr'
import type { SupabaseClient } from '@supabase/supabase-js'

/**
 * Singleton browser Supabase client.
 *
 * Without this, every component that calls createClient() would instantiate
 * a brand-new client object, each with its own connection pool, auth listener,
 * and realtime socket. On a page with Sidebar + Comments + useCommentCounts
 * that was 4+ separate clients.
 *
 * The singleton ensures the entire browser session shares one client,
 * one WebSocket connection, and one auth state listener.
 */

// Mirrors the cookieDomain logic in proxy.ts and lib/supabase/server.ts —
// all three writers must agree or the browser accumulates duplicate cookies
// with different scopes.
const COOKIE_DOMAIN = '.obi-hub.app'

// sb-<ref>-auth-token, its .0/.1 chunk cookies, and -code-verifier variants.
const AUTH_COOKIE_RE = /^sb-.+-auth-token/

// Matches @supabase/ssr's DEFAULT_COOKIE_OPTIONS maxAge (400 days).
const COOKIE_MAX_AGE = 400 * 24 * 60 * 60

function onObiHubDomain(): boolean {
  const h = window.location.hostname
  return h === 'obi-hub.app' || h.endsWith('.obi-hub.app')
}

/**
 * Rescope auth cookies from host-only to Domain=.obi-hub.app.
 *
 * The browser client historically wrote host-only cookies (no Domain
 * attribute), so a session created on obi-hub.app was invisible on
 * <slug>.obi-hub.app — fresh signups were forced to log in a second time
 * after the canonical-host redirect. Passing cookieOptions.domain below fixes
 * future writes, but an existing host-only cookie must be collapsed first:
 * browsers order same-path cookies oldest-first and cookie.parse keeps the
 * first occurrence, so a leftover host-only cookie would shadow the
 * domain-scoped one on every read, go stale, and eventually present a
 * rotated refresh token — which makes Supabase revoke the entire session.
 *
 * Keeps the first occurrence per name (exactly the value the app reads
 * today), expires the host-only variant, and rewrites the value
 * domain-scoped. Idempotent — runs on every boot, which also self-heals
 * host-only cookies written by still-open tabs running the previous build.
 */
function rescopeAuthCookiesToParentDomain(): void {
  const firstSeen = new Map<string, string>()
  for (const pair of document.cookie.split('; ')) {
    const eq = pair.indexOf('=')
    if (eq < 1) continue
    const name = pair.slice(0, eq)
    if (!AUTH_COOKIE_RE.test(name)) continue
    // Raw value, no decode/re-encode round-trip — it's written back verbatim.
    if (!firstSeen.has(name)) firstSeen.set(name, pair.slice(eq + 1))
  }

  for (const [name, value] of firstSeen) {
    // No Domain attribute = deletes only the host-only variant.
    document.cookie = `${name}=; Max-Age=0; Path=/`
    document.cookie =
      `${name}=${value}; Max-Age=${COOKIE_MAX_AGE}; Path=/; Domain=${COOKIE_DOMAIN}; SameSite=Lax`
  }
}

let _client: SupabaseClient | null = null

export function createClient(): SupabaseClient {
  if (_client) return _client

  // window is absent during the SSR pass of client components; the singleton
  // that matters is always created in the browser.
  const onProdDomain = typeof window !== 'undefined' && onObiHubDomain()
  if (onProdDomain) rescopeAuthCookiesToParentDomain()

  _client = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    // Scope auth cookies to the parent domain so one session spans the apex
    // and every brand subdomain. Localhost and Vercel previews keep the
    // host-only default.
    onProdDomain ? { cookieOptions: { domain: COOKIE_DOMAIN } } : undefined
  )

  return _client
}
